// src/components/admin/AdminEarningsSection.tsx
// ============================================================================
// ADMİN ÖDEME YÖNETİMİ PANELİ
// ============================================================================
// Bu component admin panelinde ödeme yönetimi için kullanılır.
// Bekleyen ödemeleri listeler ve onaylama işlemi yapar.
// Sadece doğrulanmış siparişler ödenebilir.
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  Clock,
  Users,
  Calendar,
  ChevronRight,
  ChevronDown,
  CreditCard,
  Check,
  X,
  Loader2,
  AlertCircle,
  AlertTriangle,
  History,
  Search,
  CheckSquare,
  ArrowRightCircle,
  Settings,
} from 'lucide-react';
import {
  getAdminPendingPayments,
  getPaymentHistory,
  approvePayment,
  bulkApprovePayments,
  carryOverPeriod,
  getMinPayout,
  setMinPayout,
  AdminPendingResponse,
  PendingUser,
  PendingPeriod,
  PaymentHistoryResponse,
  PaymentHistoryItem,
  formatCurrency,
  formatDate,
  formatShortDate,
} from '../services/earnings-api';
import CarryOverConfirmModal from '../components/admin/CarryOverConfirmModal';
import toast from 'react-hot-toast';

// Approve Modal Component
interface ApproveModalProps {
  user: PendingUser;
  period: PendingPeriod;
  onClose: () => void;
  onApprove: (transactionRef: string, note: string) => Promise<void>;
}

const ApproveModal: React.FC<ApproveModalProps> = ({ user, period, onClose, onApprove }) => {
  const [transactionRef, setTransactionRef] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    try {
      await onApprove(transactionRef, note);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="border-b p-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Ödeme Onayı</h3>
            <p className="text-sm text-gray-500">
              {user.fullName} - {period.periodName}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Ödenecek Tutar */}
          <div className="p-4 bg-green-50 rounded-xl text-center">
            <p className="text-sm text-gray-600">Ödenecek (Doğrulanmış)</p>
            <p className="text-3xl font-bold text-green-600">
              {formatCurrency(period.payableAmount ?? period.verifiedEarnings)}
            </p>
            <p className="text-xs text-gray-500 mt-1">{period.verifiedOrderCount} sipariş</p>
          </div>

          {/* Devirle birlikte kapanacak dönemler */}
          {(period.carryIns?.length ?? 0) > 0 && (
            <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl">
              <p className="text-sm font-medium text-sky-800 mb-2">
                Bu ödeme {(period.carryIns!.length + 1)} dönemi birlikte kapatacak:
              </p>
              <div className="space-y-1">
                <div className="flex justify-between text-sm text-gray-700">
                  <span>{period.periodName} (bu dönem)</span>
                  <span className="font-medium">{formatCurrency(period.verifiedEarnings)}</span>
                </div>
                {period.carryIns!.map((c) => (
                  <div key={`${c.year}-${c.month}`} className="flex justify-between text-sm text-sky-700">
                    <span>{c.periodName} — devredilmişti</span>
                    <span className="font-medium">{formatCurrency(c.verifiedEarnings)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Doğrulanmamış Uyarı */}
          {period.hasUnverifiedOrders && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <strong>{formatCurrency(period.unverifiedEarnings)}</strong> ({period.unverifiedOrderCount} sipariş) 
                  doğrulanmamış olduğu için <strong>ÖDENMEYECEKTİR</strong>.
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Info */}
          {user.primaryPaymentMethod && (
            <div className="p-3 border rounded-xl">
              <p className="text-sm text-gray-600 mb-2">Ödeme Yöntemi</p>
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium">{user.primaryPaymentMethod.accountHolderName || user.fullName}</p>
                  <p className="text-sm text-gray-500">
                    {user.primaryPaymentMethod.currency} • {user.primaryPaymentMethod.maskedAccount}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Transaction Reference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">İşlem Referans No</label>
            <input
              type="text"
              value={transactionRef}
              onChange={(e) => setTransactionRef(e.target.value)}
              placeholder="Banka transfer numarası"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Not (opsiyonel)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Admin notu"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              İptal
            </button>
            <button
              onClick={handleApprove}
              disabled={loading || (period.payableAmount ?? period.verifiedEarnings) <= 0}
              className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Onayla
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const AdminEarningsSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pending state
  const [pendingData, setPendingData] = useState<AdminPendingResponse | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Dönem odaklı görünüm: seçili dönem + toplu seçim
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [hideBelowMin, setHideBelowMin] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkRef, setBulkRef] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  
  // History state
  const [historyData, setHistoryData] = useState<PaymentHistoryResponse | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [selectedHistoryKey, setSelectedHistoryKey] = useState<string | null>(null);
  const [historySearch, setHistorySearch] = useState('');
  
  // Approve modal state
  const [approveModal, setApproveModal] = useState<{
    user: PendingUser;
    period: PendingPeriod;
  } | null>(null);

  // Devir (carry-over) state
  const [carryingOver, setCarryingOver] = useState<string | null>(null);
  const [carryOverModal, setCarryOverModal] = useState<{ user: PendingUser; period: PendingPeriod } | null>(null);
  const [minPayoutValue, setMinPayoutValue] = useState<number>(50);
  const [minPayoutInput, setMinPayoutInput] = useState<string>('50');
  const [showMinPayoutEditor, setShowMinPayoutEditor] = useState(false);
  const [savingMinPayout, setSavingMinPayout] = useState(false);

  // Fetch pending payments
  const fetchPending = async () => {
    try {
      setLoading(true);
      const data = await getAdminPendingPayments();
      setPendingData(data);
    } catch (err) {
      setError('Bekleyen ödemeler yüklenemedi');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch payment history
  const fetchHistory = async (page: number) => {
    try {
      setLoading(true);
      // Dönem kartları TÜM ödemeler üzerinden hesaplanır; sayfa başına 20 kayıt
      // çekilirse kart toplamları eksik çıkardı. Sayfalama yine duruyor —
      // ödeme sayısı bu eşiği aşarsa devreye girer.
      const data = await getPaymentHistory(page, 200);
      setHistoryData(data);
    } catch (err) {
      setError('Ödeme geçmişi yüklenemedi');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPending();
    } else {
      fetchHistory(historyPage);
    }
  }, [activeTab, historyPage]);

  // Asgari ödeme tutarı (devir eşiği) — tek genel eşik
  useEffect(() => {
    getMinPayout()
      .then((r) => {
        setMinPayoutValue(r.minPayoutAmount);
        setMinPayoutInput(String(r.minPayoutAmount));
      })
      .catch(() => {/* ayar okunamazsa varsayılan 50 gösterilir */});
  }, []);

  const handleSaveMinPayout = async () => {
    const value = parseFloat(minPayoutInput.replace(',', '.'));
    if (isNaN(value) || value < 0) {
      toast.error('Geçerli bir tutar girin.');
      return;
    }
    setSavingMinPayout(true);
    try {
      await setMinPayout(value);
      setMinPayoutValue(value);
      setShowMinPayoutEditor(false);
      await fetchPending();
    } catch (err: any) {
      toast.error(err?.message || 'Asgari tutar kaydedilemedi.');
    } finally {
      setSavingMinPayout(false);
    }
  };

  // Dönemi bir sonraki döneme devret — onay sistem içi modalda alınır (window.confirm değil)
  const handleCarryOver = async () => {
    if (!carryOverModal) return;
    const { user, period } = carryOverModal;
    const key = `${user.userId}-${period.year}-${period.month}`;

    setCarryingOver(key);
    try {
      const res = await carryOverPeriod(user.userId, period.year, period.month);
      setCarryOverModal(null);
      await fetchPending();
      toast.success(res.message);
    } catch (err: any) {
      toast.error(err?.message || 'Devir işlemi başarısız oldu.');
    } finally {
      setCarryingOver(null);
    }
  };

  // Handle approve
  const handleApprove = async (transactionRef: string, note: string) => {
    if (!approveModal) return;

    await approvePayment(
      approveModal.user.userId,
      approveModal.period.year,
      approveModal.period.month,
      { transactionReference: transactionRef, paymentNote: note }
    );

    // Refresh data
    await fetchPending();
  };

  // Ödenecek tutarı $0 olan dönemi kapat ("$0 ödendi"). Doğrulanmamış siparişleri olsa da
  // dönem listede sonsuza dek "bekleyen" kalmasın diye.
  const handleCloseZero = async (user: PendingUser, period: PendingPeriod) => {
    const unv = period.unverifiedEarnings > 0
      ? `\n\nBu dönemin doğrulanmamış ${formatCurrency(period.unverifiedEarnings)} tutarı ileride doğrulansa bile artık ÖDENMEZ.`
      : '';
    if (!window.confirm(`${user.fullName} — ${period.periodName} dönemi $0 ödendi olarak kapatılacak.${unv}\n\nOnaylıyor musun?`)) return;
    try {
      await approvePayment(user.userId, period.year, period.month, {
        paymentNote: 'Ödenecek tutar $0 — dönem kapatıldı',
        closeAtZero: true,
      });
      toast.success('Dönem kapatıldı');
      await fetchPending();
    } catch (e: any) {
      toast.error(e?.message || 'Kapatılamadı');
    }
  };

  // Filter users by search
  const filteredUsers = pendingData?.users.filter(
    (user) =>
      user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // =====================================================================
  // DÖNEM ODAKLI GÖRÜNÜM
  // Backend kullanıcı-bazlı döner (users[].pendingPeriods[]); admin ise dönem
  // dönem çalışır ("Temmuz'da Nisan dönemini ödüyorum"). Burada veriyi dönem
  // bazına çeviriyoruz — ek istek gerekmiyor.
  // =====================================================================
  type PeriodRow = { user: PendingUser; period: PendingPeriod };

  const periodGroups = React.useMemo(() => {
    const map = new Map<string, { year: number; month: number; periodName: string; paymentWindowStart: string; rows: PeriodRow[] }>();
    (pendingData?.users ?? []).forEach((user) => {
      user.pendingPeriods.forEach((period) => {
        const key = `${period.year}-${period.month}`;
        if (!map.has(key)) {
          map.set(key, {
            year: period.year,
            month: period.month,
            periodName: period.periodName,
            paymentWindowStart: period.paymentWindowStart,
            rows: [],
          });
        }
        map.get(key)!.rows.push({ user, period });
      });
    });
    return Array.from(map.entries())
      .map(([key, g]) => {
        const payable = g.rows.reduce(
          (sum, r) => sum + (r.period.payableAmount ?? r.period.verifiedEarnings), 0
        );
        // Ödeme penceresi ayın 1-5'i; 5'i geçtiyse gecikmiş sayılır
        const windowEnd = new Date(g.paymentWindowStart);
        windowEnd.setDate(windowEnd.getDate() + 5);
        return {
          key,
          ...g,
          userCount: g.rows.length,
          totalPayable: Math.round(payable * 100) / 100,
          belowMinCount: g.rows.filter((r) => r.period.isBelowMinimum).length,
          isOverdue: new Date() > windowEnd,
          // Ödeme penceresi henüz açılmadıysa dönem "sıradaki" olarak gösterilir
          windowOpen: g.rows.some((r) => r.period.paymentWindowOpen !== false),
        };
      })
      .sort((a, b) => (b.year - a.year) || (b.month - a.month));
  }, [pendingData]);

  // İlk yüklemede en güncel dönemi seç
  useEffect(() => {
    if (periodGroups.length > 0 && !periodGroups.some((g) => g.key === selectedPeriodKey)) {
      setSelectedPeriodKey(periodGroups[0].key);
      setSelectedRows(new Set());
    }
  }, [periodGroups, selectedPeriodKey]);

  const activeGroup = periodGroups.find((g) => g.key === selectedPeriodKey) ?? null;

  const visibleRows = (activeGroup?.rows ?? [])
    .filter((r) =>
      r.user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.user.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((r) => !hideBelowMin || !r.period.isBelowMinimum)
    .sort((a, b) =>
      (b.period.payableAmount ?? b.period.verifiedEarnings) -
      (a.period.payableAmount ?? a.period.verifiedEarnings)
    );

  const rowKey = (r: PeriodRow) => `${r.user.userId}-${r.period.year}-${r.period.month}`;

  const selectableRows = visibleRows.filter(
    (r) => (r.period.payableAmount ?? r.period.verifiedEarnings) > 0
  );
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedRows.has(rowKey(r)));

  const toggleRow = (r: PeriodRow) => {
    const key = rowKey(r);
    setSelectedRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedRows(allSelected ? new Set() : new Set(selectableRows.map(rowKey)));
  };

  const selectedTotal = visibleRows
    .filter((r) => selectedRows.has(rowKey(r)))
    .reduce((sum, r) => sum + (r.period.payableAmount ?? r.period.verifiedEarnings), 0);

  // Ödeme geçmişi de dönem dönem gruplanır (bekleyen ekranıyla aynı mantık)
  const historyGroups = React.useMemo(() => {
    const map = new Map<string, { year: number; month: number; periodName: string; payments: PaymentHistoryItem[] }>();
    (historyData?.payments ?? []).forEach((p) => {
      const key = `${p.year}-${p.month}`;
      if (!map.has(key)) {
        map.set(key, { year: p.year, month: p.month, periodName: p.periodName, payments: [] });
      }
      map.get(key)!.payments.push(p);
    });
    return Array.from(map.entries())
      .map(([key, g]) => ({
        key,
        ...g,
        userCount: g.payments.length,
        total: Math.round(g.payments.reduce((s, p) => s + p.paidAmount, 0) * 100) / 100,
        carriedCount: g.payments.filter((p) => (p.carriedPeriods?.length ?? 0) > 0).length,
        lastPaidAt: g.payments
          .map((p) => p.paidAtUtc)
          .sort()
          .slice(-1)[0],
      }))
      .sort((a, b) => (b.year - a.year) || (b.month - a.month));
  }, [historyData]);

  // Geçmişte de ilk yüklemede en güncel dönem seçili gelir
  useEffect(() => {
    if (historyGroups.length > 0 && !historyGroups.some((g) => g.key === selectedHistoryKey)) {
      setSelectedHistoryKey(historyGroups[0].key);
    }
  }, [historyGroups, selectedHistoryKey]);

  const activeHistoryGroup = historyGroups.find((g) => g.key === selectedHistoryKey) ?? null;

  const visibleHistoryPayments = (activeHistoryGroup?.payments ?? [])
    .filter((p) =>
      p.userFullName.toLowerCase().includes(historySearch.toLowerCase()) ||
      p.userEmail.toLowerCase().includes(historySearch.toLowerCase()) ||
      (p.transactionReference ?? '').toLowerCase().includes(historySearch.toLowerCase())
    )
    .sort((a, b) => b.paidAmount - a.paidAmount);

  // Toplu ödeme — seçili satırların hepsi tek seferde onaylanır
  const handleBulkApprove = async () => {
    const periods = visibleRows
      .filter((r) => selectedRows.has(rowKey(r)))
      .map((r) => ({ userId: r.user.userId, year: r.period.year, month: r.period.month }));

    if (periods.length === 0) return;

    setBulkSubmitting(true);
    try {
      const res: any = await bulkApprovePayments({
        periods,
        transactionReference: bulkRef || undefined,
        paymentNote: bulkNote || undefined,
      });
      const failed = (res?.results ?? []).filter((x: any) => !x.success);
      setBulkModalOpen(false);
      setBulkRef('');
      setBulkNote('');
      setSelectedRows(new Set());
      await fetchPending();

      if (failed.length > 0) {
        toast.error(`${failed.length} ödeme yapılamadı: ${failed[0].error ?? 'bilinmeyen hata'}`);
      } else {
        toast.success(res?.message ?? 'Ödemeler onaylandı');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Toplu ödeme başarısız oldu.');
    } finally {
      setBulkSubmitting(false);
    }
  };

  if (loading && !pendingData && !historyData) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Ödeme Yönetimi</h2>
            <p className="text-sm text-gray-500">Kullanıcı ödemelerini yönetin</p>
          </div>
        </div>

        {/* Asgari ödeme tutarı — bu tutarın altındaki dönemler sonraki aya devredilebilir */}
        <div className="flex items-center gap-2">
          {showMinPayoutEditor ? (
            <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-xl px-3 py-1.5">
              <span className="text-sm text-gray-600">Asgari tutar $</span>
              <input
                type="text"
                value={minPayoutInput}
                onChange={(e) => setMinPayoutInput(e.target.value)}
                className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm"
                autoFocus
              />
              <button
                onClick={handleSaveMinPayout}
                disabled={savingMinPayout}
                className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
              >
                {savingMinPayout ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button
                onClick={() => { setShowMinPayoutEditor(false); setMinPayoutInput(String(minPayoutValue)); }}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowMinPayoutEditor(true)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 hover:border-orange-400 rounded-xl text-sm text-gray-700 transition-colors"
              title="Bu tutarın altında kalan dönemler sonraki aya devredilebilir"
            >
              <Settings className="w-4 h-4 text-gray-400" />
              Asgari ödeme tutarı: <strong>{formatCurrency(minPayoutValue)}</strong>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl max-w-md">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'pending' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Clock className="w-4 h-4" />
          Bekleyen Ödemeler
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'history' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <History className="w-4 h-4" />
          Ödeme Geçmişi
        </button>
      </div>

      {/* Pending Tab */}
      {activeTab === 'pending' && pendingData && (
        <>
          {/* Özet Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Ödenebilir ✅</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(pendingData.totalVerifiedAmount)}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Doğrulanmamış ⚠️</p>
                  <p className="text-2xl font-bold text-red-500">
                    {formatCurrency(pendingData.totalUnverifiedAmount)}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Sırada ⏳</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatCurrency(pendingData.totalUpcomingAmount ?? 0)}
                  </p>
                  <p className="text-xs text-gray-500">ödeme tarihi gelmedi</p>
                </div>
                <Calendar className="w-8 h-8 text-purple-400" />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Kullanıcı Sayısı</p>
                  <p className="text-2xl font-bold text-gray-800">{pendingData.totalUsersWithPending}</p>
                </div>
                <Users className="w-8 h-8 text-gray-400" />
              </div>
            </div>
          </div>

          {/* DÖNEM KARTLARI — admin dönem dönem çalışır, önce ay seçilir */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-700">Ödeme Dönemleri</h3>
              <span className="text-xs text-gray-400">bir dönem seçin</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {periodGroups.map((g) => {
                const active = g.key === selectedPeriodKey;
                return (
                  <button
                    key={g.key}
                    onClick={() => { setSelectedPeriodKey(g.key); setSelectedRows(new Set()); }}
                    className={`flex-shrink-0 text-left px-4 py-3 rounded-xl border-2 transition-all min-w-[190px] ${
                      active
                        ? 'border-orange-500 bg-orange-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-orange-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${active ? 'bg-orange-500' : 'bg-gray-300'}`} />
                      <span className={`font-semibold ${active ? 'text-orange-700' : 'text-gray-800'}`}>
                        {g.periodName}
                      </span>
                    </div>
                    <p className="text-xl font-bold text-gray-900 mt-1 tabular-nums">
                      {formatCurrency(g.totalPayable)}
                    </p>
                    <p className="text-xs text-gray-500">{g.userCount} kullanıcı</p>
                    <p className={`text-xs mt-1 ${
                      !g.windowOpen ? 'text-purple-600 font-medium'
                        : g.isOverdue ? 'text-red-600 font-medium' : 'text-gray-400'
                    }`}>
                      {!g.windowOpen
                        ? `⏳ sırada · ödeme 1-5 ${new Date(g.paymentWindowStart).toLocaleDateString('tr-TR', { month: 'long' })}`
                        : g.isOverdue
                        ? '⏰ ödeme tarihi geçti'
                        : `ödeme: 1-5 ${new Date(g.paymentWindowStart).toLocaleDateString('tr-TR', { month: 'long' })}`}
                    </p>
                    {g.belowMinCount > 0 && (
                      <p className="text-xs text-sky-600 mt-0.5">{g.belowMinCount} tanesi eşik altı</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* SEÇİLİ DÖNEMİN KULLANICI TABLOSU */}
          {activeGroup && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="border-b border-gray-200 p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <h3 className="font-semibold text-gray-800">{activeGroup.periodName} dönemi</h3>
                  <p className="text-xs text-gray-500">
                    {activeGroup.userCount} kullanıcı · toplam {formatCurrency(activeGroup.totalPayable)}
                  </p>
                </div>

                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Kullanıcı ara..."
                    className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-52 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hideBelowMin}
                    onChange={(e) => setHideBelowMin(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Eşik altını gizle
                </label>

                <button
                  onClick={() => setBulkModalOpen(true)}
                  disabled={selectedRows.size === 0}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Check className="w-4 h-4" />
                  Seçilenleri Öde ({selectedRows.size})
                  {selectedRows.size > 0 && <span className="opacity-90">· {formatCurrency(selectedTotal)}</span>}
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-gray-300" />
                      </th>
                      <th className="px-4 py-3 font-semibold">Kullanıcı</th>
                      <th className="px-4 py-3 font-semibold text-right">Ödenecek</th>
                      <th className="px-4 py-3 font-semibold text-right">Devir</th>
                      <th className="px-4 py-3 font-semibold text-right">Doğrulanmamış</th>
                      <th className="px-4 py-3 font-semibold">Ödeme yöntemi</th>
                      <th className="px-4 py-3 font-semibold text-right">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visibleRows.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Bu dönemde kayıt yok</td></tr>
                    ) : visibleRows.map((r) => {
                      const payable = r.period.payableAmount ?? r.period.verifiedEarnings;
                      const key = rowKey(r);
                      const carryIns = r.period.carryIns ?? [];
                      return (
                        <tr key={key} className={`hover:bg-gray-50 ${selectedRows.has(key) ? 'bg-orange-50/40' : ''}`}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedRows.has(key)}
                              onChange={() => toggleRow(r)}
                              disabled={payable <= 0}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-800">{r.user.fullName}</p>
                            <p className="text-xs text-gray-500">{r.user.email}</p>
                            {carryIns.length > 0 && (
                              <p className="text-[11px] text-sky-700 mt-0.5">
                                ↪ {carryIns.map((c) => c.periodName).join(', ')} devredilmişti
                              </p>
                            )}
                            {r.period.isBelowMinimum && (
                              <span className="inline-block mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                asgari {formatCurrency(r.period.minPayoutAmount)} altı
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-semibold text-green-600">
                            {formatCurrency(payable)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-sky-700">
                            {(r.period.carryInAmount ?? 0) > 0 ? `+${formatCurrency(r.period.carryInAmount!)}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-red-500">
                            {r.period.unverifiedEarnings > 0 ? formatCurrency(r.period.unverifiedEarnings) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {r.user.primaryPaymentMethod ? (
                              <span className="text-xs text-gray-600 flex items-center gap-1">
                                <CreditCard className="w-3 h-3 text-gray-400" />
                                {r.user.primaryPaymentMethod.currency} · {r.user.primaryPaymentMethod.maskedAccount}
                              </span>
                            ) : (
                              <span className="text-xs text-red-600">⚠️ tanımlı değil</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              {payable <= 0 ? (
                                <button
                                  onClick={() => handleCloseZero(r.user, r.period)}
                                  title="Ödenecek tutar yok — dönemi $0 ödendi olarak kapat"
                                  className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-xs font-medium"
                                >
                                  Kapat ($0)
                                </button>
                              ) : (
                                <button
                                  onClick={() => setApproveModal({ user: r.user, period: r.period })}
                                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium"
                                >
                                  Öde
                                </button>
                              )}
                              {r.period.isBelowMinimum && (
                                <button
                                  onClick={() => setCarryOverModal({ user: r.user, period: r.period })}
                                  className="px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-medium flex items-center gap-1"
                                  title="Sonraki döneme ertele"
                                >
                                  <ArrowRightCircle className="w-3 h-3" />
                                  Devret
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {periodGroups.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">
              <CheckSquare className="w-12 h-12 mx-auto mb-3 text-green-500" />
              <p>Bekleyen ödeme bulunmuyor</p>
            </div>
          )}
        </>
      )}

      {/* History Tab */}
      {activeTab === 'history' && historyData && (
        <>
          {/* Özet */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Toplam Ödenen</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(historyGroups.reduce((s, g) => s + g.total, 0))}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Ödeme Dönemi</p>
                  <p className="text-2xl font-bold text-gray-800">{historyGroups.length}</p>
                </div>
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Toplam Ödeme</p>
                  <p className="text-2xl font-bold text-gray-800">{historyData.total}</p>
                </div>
                <History className="w-8 h-8 text-gray-400" />
              </div>
            </div>
          </div>

          {/* DÖNEM KARTLARI — bekleyen ekranıyla aynı düzen */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-700">Ödeme Dönemleri</h3>
              <span className="text-xs text-gray-400">bir dönem seçin</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {historyGroups.map((g) => {
                const active = g.key === selectedHistoryKey;
                return (
                  <button
                    key={g.key}
                    onClick={() => setSelectedHistoryKey(g.key)}
                    className={`flex-shrink-0 text-left px-4 py-3 rounded-xl border-2 transition-all min-w-[190px] ${
                      active
                        ? 'border-green-500 bg-green-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${active ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <span className={`font-semibold ${active ? 'text-green-700' : 'text-gray-800'}`}>
                        {g.periodName}
                      </span>
                    </div>
                    <p className="text-xl font-bold text-gray-900 mt-1 tabular-nums">
                      {formatCurrency(g.total)}
                    </p>
                    <p className="text-xs text-gray-500">{g.userCount} kişiye ödendi</p>
                    {g.lastPaidAt && (
                      <p className="text-xs text-gray-400 mt-1">son: {formatShortDate(g.lastPaidAt)}</p>
                    )}
                    {g.carriedCount > 0 && (
                      <p className="text-xs text-sky-600 mt-0.5">{g.carriedCount} devirli ödeme</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* SEÇİLİ DÖNEMİN ÖDEME TABLOSU */}
          {activeHistoryGroup && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="border-b border-gray-200 p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <h3 className="font-semibold text-gray-800">{activeHistoryGroup.periodName} ödemesi</h3>
                  <p className="text-xs text-gray-500">
                    {activeHistoryGroup.userCount} kişi · toplam {formatCurrency(activeHistoryGroup.total)}
                  </p>
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    placeholder="Kullanıcı veya referans ara..."
                    className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Kullanıcı</th>
                      <th className="px-4 py-3 font-semibold text-right">Ödenen</th>
                      <th className="px-4 py-3 font-semibold text-right">Devir</th>
                      <th className="px-4 py-3 font-semibold text-right">Sipariş</th>
                      <th className="px-4 py-3 font-semibold">Ödeme tarihi</th>
                      <th className="px-4 py-3 font-semibold">Referans</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visibleHistoryPayments.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Kayıt bulunamadı</td></tr>
                    ) : visibleHistoryPayments.map((payment) => {
                      const carried = payment.carriedPeriods ?? [];
                      return (
                        <tr key={payment.id} className="hover:bg-gray-50 align-top">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-800">{payment.userFullName}</p>
                            <p className="text-xs text-gray-500">{payment.userEmail}</p>
                            {carried.length > 0 && (
                              <div className="mt-1.5 p-2 bg-sky-50 border border-sky-200 rounded-lg space-y-0.5 inline-block">
                                <p className="text-[11px] font-medium text-sky-800">
                                  {carried.length + 1} dönem bu ödemeyle kapandı
                                </p>
                                <div className="flex justify-between gap-4 text-[11px] text-gray-600">
                                  <span>{payment.periodName} (ödeme dönemi)</span>
                                  <span className="tabular-nums">{formatCurrency(payment.ownAmount ?? 0)}</span>
                                </div>
                                {carried.map((c) => (
                                  <div key={c.id} className="flex justify-between gap-4 text-[11px] text-sky-700">
                                    <span>{c.periodName} — devredilmişti</span>
                                    <span className="tabular-nums">{formatCurrency(c.paidAmount)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-semibold text-green-600">
                            {formatCurrency(payment.paidAmount)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-sky-700">
                            {(payment.carryInAmount ?? 0) > 0 ? `+${formatCurrency(payment.carryInAmount!)}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                            {payment.paidOrderCount}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{formatShortDate(payment.paidAtUtc)}</td>
                          <td className="px-4 py-3">
                            {payment.transactionReference ? (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono">
                                {payment.transactionReference}
                              </span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {historyGroups.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">
              <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Henüz ödeme yapılmamış</p>
            </div>
          )}

          {/* Sayfalama — çok fazla ödeme birikirse */}
          {historyData.totalPages > 1 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
              <button
                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                disabled={historyPage === 1}
                className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Önceki
              </button>
              <span className="text-sm text-gray-600">
                Sayfa {historyPage} / {historyData.totalPages}
              </span>
              <button
                onClick={() => setHistoryPage((p) => Math.min(historyData.totalPages, p + 1))}
                disabled={historyPage === historyData.totalPages}
                className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Sonraki
              </button>
            </div>
          )}
        </>
      )}

      {/* Approve Modal */}
      {approveModal && (
        <ApproveModal
          user={approveModal.user}
          period={approveModal.period}
          onClose={() => setApproveModal(null)}
          onApprove={handleApprove}
        />
      )}

      {/* TOPLU ÖDEME ONAYI */}
      {bulkModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={bulkSubmitting ? undefined : () => setBulkModalOpen(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="border-b p-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-800">Toplu Ödeme Onayı</h3>
                <p className="text-sm text-gray-500">{activeGroup?.periodName} dönemi</p>
              </div>
              <button
                onClick={() => setBulkModalOpen(false)}
                disabled={bulkSubmitting}
                className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="p-4 bg-green-50 rounded-xl text-center">
                <p className="text-sm text-gray-600">{selectedRows.size} kullanıcıya ödenecek</p>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(selectedTotal)}</p>
              </div>

              <div className="max-h-48 overflow-y-auto border rounded-xl divide-y divide-gray-100">
                {visibleRows
                  .filter((r) => selectedRows.has(rowKey(r)))
                  .map((r) => (
                    <div key={rowKey(r)} className="px-3 py-2 flex items-center justify-between text-sm">
                      <div>
                        <p className="text-gray-800">{r.user.fullName}</p>
                        {!r.user.primaryPaymentMethod && (
                          <p className="text-[11px] text-red-600">⚠️ ödeme yöntemi tanımlı değil</p>
                        )}
                        {(r.period.carryIns?.length ?? 0) > 0 && (
                          <p className="text-[11px] text-sky-700">
                            +{r.period.carryIns!.length} devredilmiş dönem birlikte kapanacak
                          </p>
                        )}
                      </div>
                      <span className="font-medium tabular-nums text-green-700">
                        {formatCurrency(r.period.payableAmount ?? r.period.verifiedEarnings)}
                      </span>
                    </div>
                  ))}
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                Yalnızca <strong>doğrulanmış</strong> tutarlar ödenir. Devredilmiş dönemler aynı ödemeyle kapanır.
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">İşlem Referans No</label>
                <input
                  type="text"
                  value={bulkRef}
                  onChange={(e) => setBulkRef(e.target.value)}
                  placeholder="Banka transfer numarası (opsiyonel)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Not (opsiyonel)</label>
                <input
                  type="text"
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                  placeholder="Admin notu"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setBulkModalOpen(false)}
                  disabled={bulkSubmitting}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  İptal
                </button>
                <button
                  onClick={handleBulkApprove}
                  disabled={bulkSubmitting}
                  className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {bulkSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Ödeniyor...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      {selectedRows.size} Ödemeyi Onayla
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Devir onayı — tarayıcı kutusu değil, sistemin kendi bildirimi */}
      {carryOverModal && (
        <CarryOverConfirmModal
          userName={carryOverModal.user.fullName}
          userEmail={carryOverModal.user.email}
          periodName={carryOverModal.period.periodName}
          amountText={formatCurrency(
            carryOverModal.period.payableAmount ?? carryOverModal.period.verifiedEarnings
          )}
          minPayoutText={formatCurrency(carryOverModal.period.minPayoutAmount ?? minPayoutValue)}
          kindLabel="Kullanıcı kazancı"
          loading={carryingOver !== null}
          onCancel={() => setCarryOverModal(null)}
          onConfirm={handleCarryOver}
        />
      )}
    </div>
  );
};

export default AdminEarningsSection;